import React, { useState, useCallback, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import useReorderDragDropBackend from 'hooks/useReorderDragDropBackend';
import cloneDeep from 'lodash.clonedeep';

import { IFAQItem } from './shared/shared';
import FAQLoader from './FAQLoader';
import FAQItem from './FAQItem';

interface IFAQAccordionProps {
  faqData: IFAQItem[] | null;
  isAdminRole: boolean;
  loading?: boolean;
  updateFaq: (faqChanged: IFAQItem[]) => any;
}

const FAQAccordion: React.FC<IFAQAccordionProps> = (props) => {
  const { faqData, loading, isAdminRole, updateFaq } = props;

  const [expandedRow, setExpandedRow] = useState<IFAQItem | null>(null);
  const toggleExpand = (faqItem: IFAQItem): void => {
    setExpandedRow(expandedRow && expandedRow === faqItem ? null : faqItem);
  };

  const [draggedItems, setDraggedItems] = useState<IFAQItem[]>([]);

  useEffect(() => {
    if (faqData) setDraggedItems(faqData);
  }, [faqData]);

  const [DragDropBackend, backendOptions] = useReorderDragDropBackend();

  const onEditItem = (index: number, item: IFAQItem): void => {
    draggedItems[index] = item;
    updateFaq(draggedItems);
    toggleExpand(item);
  };

  const onDeleteItem = (index: number): void => {
    draggedItems.splice(index, 1);
    updateFaq(draggedItems);
  };

  const changeOrder = useCallback(
    (start: number, end: number) => {
      const dragged = cloneDeep(draggedItems);
      dragged.splice(start, 1);
      dragged.splice(end, 0, draggedItems[start]);
      setDraggedItems(dragged);
    },
    [draggedItems]
  );

  return (
    <>
      {draggedItems && (
        <DndProvider backend={DragDropBackend} options={backendOptions}>
          {draggedItems.map(
            (faqItem, index): JSX.Element => (
              <FAQItem
                isAdminRole={isAdminRole}
                key={faqItem.title}
                faqItem={faqItem}
                index={index}
                isActive={faqItem === expandedRow}
                toggleExpand={(): void => toggleExpand(faqItem)}
                changeOrder={changeOrder}
                afterDrop={(): void => {
                  updateFaq(draggedItems);
                }}
                onEditItem={(item: IFAQItem): void => onEditItem(index, item)}
                onDeleteItem={(): void => onDeleteItem(index)}
              />
            )
          )}
        </DndProvider>
      )}
      {!faqData && loading && <FAQLoader />}
    </>
  );
};

export default FAQAccordion;
